import { observer } from 'mobx-react';
import React from 'react';

import { SplitLineItemResult } from '../.././pages/page-payment-success/page-payment-success-view-model';
import { Formatter } from '../../../Shared/utils/formatter';
import successIcon from '../../assets/svg/icon-success.svg';
import errorIcon from '../../assets/svg/icon-error.svg';
import { splitLineItemSuccessStatuses } from '../../utils/webgiving-utils';
import { PaymentLabel } from '../../webgiving-generated';
import { Alert } from '../alert/alert';
import { SvgIcon } from '../svg-icon';
import { Text } from '../text'
import * as styles from './line-item-summary-table.less';


type LineItemSummaryTableProps = {
    splitLineItemResults: SplitLineItemResult[];
    paymentLabel: PaymentLabel;
}

export const isFailedPayment = (lineItem: SplitLineItemResult) => !splitLineItemSuccessStatuses.includes(lineItem.paymentStatus);

export const LineItemSummaryTable = observer(({ splitLineItemResults = [], paymentLabel }: LineItemSummaryTableProps) => {

    const { NounPluralLowerCase } = paymentLabel;

    const getSummaryTable = (paymentResults: SplitLineItemResult[]) => {
        let total = 0;
        const currencySymbol = '$';
        const paymentSummaryTable = paymentResults.map(({ FundName, Amount, FundKey, paymentStatus }) => {
            total += Amount;
            const isPaymentSuccess = splitLineItemSuccessStatuses.includes(paymentStatus);
            return (
                <div className={styles.tiledRow} key={`${FundKey}-${FundName}`}>
                    <div>{isPaymentSuccess ? <SvgIcon svg={successIcon} className={styles.successIcon} /> : <SvgIcon svg={errorIcon} className={styles.errorIcon} />}</div>
                    <div className={styles.fundName}>{FundName}</div>
                    <div className={styles.amount}>{currencySymbol}{Formatter.formatNumberForDisplay(Amount, 2)}</div>
                </div>
            )
        });

        return (
            <div className={styles.table}>
                {paymentSummaryTable}
                <div className={styles.total}>
                    <div className={styles.totalText}><Text>Total</Text></div>
                    <div className={styles.totalAmount}>{currencySymbol}{Formatter.formatNumberForDisplay(total, 2)}</div>
                </div>
            </div>
        );
    }

    const failedPayments = splitLineItemResults.filter(isFailedPayment);
    const successfulPayments = splitLineItemResults.filter((lineItem) => !isFailedPayment(lineItem));
    const hasPartialSuccessfulPayments = successfulPayments.length > 0 && failedPayments.length > 0;

    if (hasPartialSuccessfulPayments) {
        const noOfFailedGifts = failedPayments.length;
        const noOfTotalGifts = splitLineItemResults.length;
        const alertMessage = <Text params={{ noOfFailedGifts, noOfTotalGifts}}>{`{{noOfFailedGifts}} of the {{noOfTotalGifts}} ${NounPluralLowerCase} were not successful.`}</Text>
        return (
            <>
                <div className={styles.partialStatusText}><Text>Successful transactions</Text></div>
                {getSummaryTable(successfulPayments)}
                <div className={styles.errorAlertContainer}>
                    <Alert content={alertMessage} />
                </div>
                <div className={styles.partialStatusText}><Text>Failed transactions</Text></div>
                {getSummaryTable(failedPayments)}
            </>
        );
    }

    return (
        <>
            {getSummaryTable(splitLineItemResults)}
        </>
    );
})